import { useCallback, useEffect, useState } from 'react';
import { Storage } from '../../constants/storage';

const STORAGE_ID_KEY = Storage.dev_agent_id;
const STORAGE_NAME_KEY = Storage.dev_agent_name;
const URL_PARAM = 'agent_id';

const readStorage = (key) => {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(key) || null;
    } catch {
        return null;
    }
};

const writeStorage = (key, value) => {
    if (typeof window === 'undefined') return;
    try {
        if (value) window.localStorage.setItem(key, value);
        else window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
};

const readUrlAgent = () => {
    if (typeof window === 'undefined') return null;
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get(URL_PARAM) || null;
    } catch {
        return null;
    }
};

const syncUrlAgent = (value) => {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        if (value) url.searchParams.set(URL_PARAM, value);
        else url.searchParams.delete(URL_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
    } catch {
        // ignore
    }
};

const resolveInitialValue = () => {
    const fromUrl = readUrlAgent();
    const fromStorageId = readStorage(STORAGE_ID_KEY);
    const fromStorageName = readStorage(STORAGE_NAME_KEY);
    if (fromUrl && fromUrl !== fromStorageId) {
        writeStorage(STORAGE_ID_KEY, fromUrl);
        writeStorage(STORAGE_NAME_KEY, null);
        return { id: fromUrl, name: null };
    }
    if (!fromUrl && fromStorageId) {
        syncUrlAgent(fromStorageId);
    }
    return { id: fromStorageId, name: fromStorageName };
};

export function useAgentSelection() {
    const [selection, setSelection] = useState(resolveInitialValue);

    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_ID_KEY) {
                const nextId = e.newValue || null;
                syncUrlAgent(nextId);
                setSelection((prev) => ({ id: nextId, name: nextId ? prev.name : null }));
            } else if (e.key === STORAGE_NAME_KEY) {
                setSelection((prev) => ({ ...prev, name: e.newValue || null }));
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const setAgent = useCallback((next) => {
        const id = next?.id || null;
        const name = id ? (next?.name || null) : null;
        writeStorage(STORAGE_ID_KEY, id);
        writeStorage(STORAGE_NAME_KEY, name);
        syncUrlAgent(id);
        setSelection({ id, name });
    }, []);

    const setAgentId = useCallback((id) => setAgent({ id }), [setAgent]);
    const clearAgentId = useCallback(() => setAgent(null), [setAgent]);

    const hydrateAgentName = useCallback((name) => {
        if (!name) return;
        writeStorage(STORAGE_NAME_KEY, name);
        setSelection((prev) => (prev.name === name ? prev : { ...prev, name }));
    }, []);

    return {
        agentId: selection.id,
        agentName: selection.name,
        setAgent,
        setAgentId,
        clearAgentId,
        hydrateAgentName,
    };
}
